// src/pages/ViewBOMs.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Table, Form, Button, Spinner, Badge, Row, Col } from "react-bootstrap";
import { Base_URL } from "../url";
import { useAuth } from "../Context/AuthContext";
import "../Styling/AllTables.css"
import { FaSearch } from "react-icons/fa";



export default function ViewBOMs() {
  const { user } = useAuth(); // in case you add auth headers later
  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [sort, setSort] = useState({ key: "bomId", dir: "asc" });

  const safeFetchJson = useCallback(async (url, init) => {
    const res = await fetch(url, {
      headers: { Accept: "application/json" },
      ...init,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`HTTP ${res.status} ${res.statusText} – ${text}`);
    }
    const ct = res.headers.get("content-type") || "";
    if (!ct.includes("application/json")) {
      const text = await res.text();
      return text ? JSON.parse(text) : [];
    }
    return res.json();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const items = await safeFetchJson(`${Base_URL}/BOM/getAllBoms`);
      const arr = Array.isArray(items) ? items : [];
      setData(arr);
      setFiltered(arr);
    } catch (e) {
      console.error(e);
      setErr(e.message || "Failed to load BOMs");
    } finally {
      setLoading(false);
    }
  }, [safeFetchJson]);

  useEffect(() => {
    load();
  }, [load]);

  // Search across a few key fields
  useEffect(() => {
    const term = q.trim().toLowerCase();
    if (!term) return setFiltered(data);
    setFiltered(
      data.filter((b) => {
        const hay = [
          b.bomId,
          b.parentItemCodeId,
          b.componentItemCodeId,
          b.unitCode,
          b.officeCode,
          b.bomType,
          b.createdBy,
        ]
          .map((x) => (x ?? "").toString().toLowerCase())
          .join("|");
        return hay.includes(term);
      })
    );
  }, [q, data]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const { key, dir } = sort;
    arr.sort((a, b) => {
      const va = a?.[key];
      const vb = b?.[key];

      // numbers
      if (typeof va === "number" && typeof vb === "number") {
        return dir === "asc" ? va - vb : vb - va;
      }
      // dates if both parse
      const da = va ? new Date(va) : null;
      const db = vb ? new Date(vb) : null;
      if (da instanceof Date && !isNaN(da) && db instanceof Date && !isNaN(db)) {
        return dir === "asc" ? da - db : db - da;
      }
      // strings
      const sa = (va ?? "").toString().toLowerCase();
      const sb = (vb ?? "").toString().toLowerCase();
      if (sa < sb) return dir === "asc" ? -1 : 1;
      if (sa > sb) return dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sort]);

  const setSortKey = (key) => {
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    );
  };

  const fmtDate = (val) => {
    if (!val) return "-";
    const d = new Date(val);
    return isNaN(d.getTime()) ? val : d.toLocaleDateString();
  };

  const fmtTime = (val) => (val ? val.toString() : "-");

  return (
    <div className="container py-3">
          <div className="header-background">
         <h4 className="header-list-title">BOM</h4>
    
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <div className="search-wrapper wide position-relative">
          <FaSearch className="search-icon"
            style={{
              position: "absolute",
              top: "50%",
              left: "10px",
              transform: "translateY(-50%)",
              color: "#888",
              zIndex: 2, 
              pointerEvents: "none",
            }}
          />
          <Form.Control
            className="search-input"
            placeholder="Search by ID, Name, Description, Method, Basis, Unit…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
    
        <Button
          className="gradient-btn"
          style={{
            paddingLeft:'5px',
            paddingRight:'2px'
          }}
          // variant="outline-secondary"
          onClick={load}
          disabled={loading}
        >
          {loading ? <Spinner animation="border" size="sm" /> : "Refresh"}
        </Button>
      </div>
    </div>
      {err && (
        <div className="alert alert-danger py-2">
          <strong>Error:</strong> {err}
        </div>
      )}
      

      <div className="table-wrapper">
        <div className="table-scroll-container">
        <Table hover bordered size="sm" className="data-table">
          <thead>
            <tr>
              <Th onClick={() => setSortKey("bomId")} active={sort.key === "bomId"} dir={sort.dir}>
                BOM ID
              </Th>
              <Th onClick={() => setSortKey("parentItemCodeId")} active={sort.key === "parentItemCodeId"} dir={sort.dir}>
                Parent Item
              </Th>
              <Th onClick={() => setSortKey("componentItemCodeId")} active={sort.key === "componentItemCodeId"} dir={sort.dir}>
                Component Item
              </Th>
              <Th onClick={() => setSortKey("quantityPerParent")} active={sort.key === "quantityPerParent"} dir={sort.dir}>
                Qty / Parent
              </Th>
              <Th onClick={() => setSortKey("unitCode")} active={sort.key === "unitCode"} dir={sort.dir}>
                Unit
              </Th>
              <Th onClick={() => setSortKey("scrapPercentage")} active={sort.key === "scrapPercentage"} dir={sort.dir}>
                Scrap %
              </Th>
              <Th onClick={() => setSortKey("effectiveFromDate")} active={sort.key === "effectiveFromDate"} dir={sort.dir}>
                Effective From
              </Th>
              <Th onClick={() => setSortKey("effectiveToDate")} active={sort.key === "effectiveToDate"} dir={sort.dir}>
                Effective To
              </Th>
              <Th onClick={() => setSortKey("bomType")} active={sort.key === "bomType"} dir={sort.dir}>
                Type
              </Th>
              <Th onClick={() => setSortKey("officeCode")} active={sort.key === "officeCode"} dir={sort.dir}>
                Office
              </Th>
              <Th onClick={() => setSortKey("active")} active={sort.key === "active"} dir={sort.dir}>
                Active
              </Th>
              <Th onClick={() => setSortKey("createdBy")} active={sort.key === "createdBy"} dir={sort.dir}>
                Created By
              </Th>
              <Th onClick={() => setSortKey("createdDate")} active={sort.key === "createdDate"} dir={sort.dir}>
                Created Date
              </Th>
              <Th onClick={() => setSortKey("createdTime")} active={sort.key === "createdTime"} dir={sort.dir}>
                Created Time
              </Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={15} className="text-center py-4">
                  <Spinner animation="border" />
                </td>
              </tr>
            ) : sorted.length === 0 ? (
              <tr>
                <td colSpan={15} className="text-center py-3">
                  No BOMs found.
                </td>
              </tr>
            ) : (
              sorted.map((b) => (
                <tr key={b.bomId}>
                  <td className="fw-semibold">{b.bomId}</td>
                  <td>{b.parentItemName}</td>
                  <td>{b.componentItemName}</td>
                  <td>{b.quantityPerParent ?? "-"}</td>
                  <td>{b.unitCode}</td>
                  <td>{b.scrapPercentage ?? "-"}</td>
                  <td>{fmtDate(b.effectiveFromDate)}</td>
                  <td>{fmtDate(b.effectiveToDate)}</td>
                  <td>{b.bomTypeDescription}</td>
                  <td>{b.officeName}</td>
                  <td>
                    {b.active ? (
                      <Badge bg="success">Active</Badge>
                    ) : (
                      <Badge bg="secondary">Inactive</Badge>
                    )}
                  </td>
                  <td>{b.createdBy ?? "-"}</td>
                  <td>{fmtDate(b.createdDate)}</td>
                  <td>{fmtTime(b.createdTime)}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
        </div>
      </div>
    </div>
  );
}

function Th({ children, onClick, active, dir }) {
  return (
    <th
      role="button"
      onClick={onClick}
      className={`user-select-none ${onClick ? "text-nowrap" : ""}`}
      title={onClick ? "Click to sort" : undefined}
    >
      <span className="d-inline-flex align-items-center gap-1">
        {children}
        {onClick && <SortIcon active={active} dir={dir} />}
      </span>
    </th>
  );
}

function SortIcon({ active, dir }) {
  return <span style={{ opacity: active ? 1 : 0.3 }}>{dir === "asc" ? "▲" : "▼"}</span>;
}
